import React from 'react'
import { Box, Typography, ButtonBase, Button } from '@mui/material'
import DoneIcon from '@mui/icons-material/Done'
import { useReceivingStore } from 'store/receiving'
import type { ProductType } from 'store/receiving'
import { useIsTablet } from 'lib/utils/mediaQueries'

import Biologic from 'assets/icons/svgs/ImplantableBiological.svg'
import Hardware from 'assets/icons/svgs/ImplantableHardware.svg'
import Other from 'assets/icons/svgs/ImplantableOther.svg'

import styles from './ProductTypeSelection.module.scss'
import type { ProductTypeSelectionProps } from './ProductTypeSelection.types'

const ProductTypeSelection: React.FC<ProductTypeSelectionProps> = ({
  onNext,
  onBack,
}) => {
  const productType = useReceivingStore((s) => s.productType)
  const setProductType = useReceivingStore((s) => s.setProductType)
  const ownershipType = useReceivingStore((s) => s.ownershipType)
  const isMobile = useIsTablet()

  const productOptions: Array<{
    label: string
    value: ProductType
    icon: string
    description: string
  }> = [
    {
      label: 'Biologic',
      value: 'Biologic',
      icon: Biologic,
      description: 'Biological implants and tissue products',
    },
    {
      label: 'Hardware',
      value: 'Hardware',
      icon: Hardware,
      description: 'Metal implants, screws, plates, and devices',
    },
    {
      label: 'Other',
      value: 'Other',
      icon: Other,
      description: 'Consumables and other medical products',
    },
  ]

  const handleSelection = (type: ProductType) => {
    setProductType(type)
    setTimeout(() => {
      onNext()
    }, 300)
  }

  return (
    <Box className={styles.productTypeSelection}>
      <Typography variant="h5" align="center" className={styles.title}>
        Select Product Type
      </Typography>

      <Typography
        variant="body1"
        align="center"
        color="text.secondary"
        className={styles.subtitle}
      >
        Receiving {ownershipType} items
      </Typography>

      <Typography
        variant="body2"
        align="center"
        color="text.secondary"
        className={`${styles.subtitle} ${styles.description}`}
      >
        Choose the type of products you'll be capturing in this session.
      </Typography>

      <Box className={styles.productOptions}>
        {productOptions.map((option) => (
          <ButtonBase
            key={option.value}
            className={`${styles.productOption} ${
              productType === option.value ? styles.selected : ''
            }`}
            disableRipple
            onClick={() => handleSelection(option.value)}
          >
            <Box className={styles.optionContent}>
              <Box className={styles.optionHeader}>
                <Box className={styles.optionCircle}>
                  {productType === option.value ? (
                    <DoneIcon className={styles.checkIcon} />
                  ) : (
                    <img
                      src={option.icon}
                      alt={option.label}
                      className={`${styles.optionIcon} ${
                        isMobile ? styles.mobile : styles.desktop
                      }`}
                    />
                  )}
                </Box>
                <Typography variant="h6" className={styles.optionTitle}>
                  {option.label}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.optionDescription}
              >
                {option.description}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>

      {onBack && (
        <Box className={styles.actions}>
          <Button
            variant="outlined"
            onClick={onBack}
            className={styles.backButton}
          >
            Back
          </Button>
          <Box className={styles.spacer} />
        </Box>
      )}
    </Box>
  )
}

export default ProductTypeSelection
